import {ChangeEvent, FC, FocusEventHandler, KeyboardEventHandler} from 'react';
import {SxProps, TextField, TextFieldProps, Theme} from '@mui/material';
import {Control, Controller, FieldValues, RegisterOptions} from 'react-hook-form';

interface IControlledInput {
  control: Control<any, any>;
  rules?: Omit<RegisterOptions<FieldValues, string>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
  name: string;
  label?: string;
  size?: 'medium' | 'small';
  multiline?: boolean;
  additionalFunc?: <T>(event?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => T | void;
  sx?: SxProps<Theme>;
  conditionalSxError?: SxProps<Theme>;
  disabled?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  defaultValue?: string;
  rows?: number;
  InputProps?: TextFieldProps['InputProps'];
  onKeyUp?: KeyboardEventHandler<HTMLDivElement>;
  type?: string;
  search?: (arg: string) => void;
  onBlur?: FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  [key: string]: any;
}

const ControlledInput: FC<IControlledInput> = (props) => {
  const {
    control,
    rules,
    name,
    label,
    size,
    multiline,
    disabled,
    additionalFunc,
    sx,
    placeholder,
    fullWidth,
    defaultValue,
    rows,
    InputProps,
    onKeyUp,
    type,
    conditionalSxError,
    search,
    onBlur: eternalOnBlur,
    ...rest
  } = props;

  return (
    <Controller
      name={name}
      rules={rules}
      control={control}
      render={({field: {value, onChange, ref, onBlur}, fieldState: {error}}) => (
        <TextField
          type={type}
          disabled={disabled}
          autoComplete="off"
          onKeyUp={onKeyUp}
          sx={!!conditionalSxError && !!error ? conditionalSxError : sx}
          multiline={multiline}
          size={size}
          onBlur={(e) => {
            onBlur();
            eternalOnBlur && eternalOnBlur(e);
          }}
          defaultValue={defaultValue}
          label={label}
          placeholder={placeholder}
          fullWidth={fullWidth}
          rows={rows}
          value={value}
          onChange={(e) => {
            onChange(e);
            search && search(e.currentTarget.value);
            additionalFunc && additionalFunc(e);
          }}
          inputRef={ref}
          error={!!error}
          helperText={error ? error.message : ''}
          InputProps={InputProps}
          inputProps={{...rest}}
        />
      )}
    />
  );
};

export default ControlledInput;
